import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
    Phone,
    Mail,
    MapPin,
    Navigation,
    CreditCard,
    ShoppingBag,
    Calendar,
    Building,
    Users
} from 'lucide-react';
import { getPartyById, getRecentOrdersForParty } from '@/app/services/partyService';
import { useAuthStore } from '@/app/store/authStore';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { MobileCardGroup, MobileCardRow } from '@/components/ui/MobileCard';
import { MobileBadge } from '@/components/ui/MobileBadge';
import { MobileSkeleton } from '@/components/ui/MobileSkeleton';

export default function PartyDetailPage() {
    const { id } = useParams<{ id: string }>();
    const user = useAuthStore((state) => state.user);

    // Get user's site IDs for querying vouchers across all accessible sites
    const userSiteIds = user?.sites?.map(s => s.id) || [];

    const { data: party, isLoading: partyLoading } = useQuery({
        queryKey: ['party', id],
        queryFn: () => getPartyById(Number(id)),
        enabled: !!id,
    });

    const { data: recentOrders = [], isLoading: orderLoading } = useQuery({
        queryKey: ['party-recent-orders', id, userSiteIds],
        queryFn: () => getRecentOrdersForParty(Number(id), userSiteIds, 5),
        enabled: !!id && userSiteIds.length > 0,
    });

    if (partyLoading) {
        return (
            <div className="flex flex-col min-h-full">
                <MobileHeader title="Loading..." showBack />
                <div className="p-4 space-y-4">
                    <MobileSkeleton className="h-32 w-full rounded-xl" />
                    <MobileSkeleton className="h-48 w-full rounded-xl" />
                </div>
            </div>
        );
    }

    if (!party) {
        return (
            <div className="flex flex-col min-h-full">
                <MobileHeader title="Not Found" showBack />
                <div className="flex-1 flex items-center justify-center p-4">
                    <p className="text-muted-foreground">Party not found</p>
                </div>
            </div>
        );
    }

    const creditLimitDisplay = party.credit_limit_type === 'no_limit'
        ? 'No Limit'
        : `₹${parseFloat(party.credit_limit_amount || '0').toLocaleString()}`;

    const handleOpenMaps = () => {
        if (party.geo_location) {
            window.open(party.geo_location, '_blank');
        }
    };

    const handleCall = () => {
        if (party.phone) {
            window.location.href = `tel:${party.phone}`;
        }
    };

    const handleEmail = () => {
        if (party.email) {
            window.location.href = `mailto:${party.email}`;
        }
    };

    return (
        <div className="flex flex-col min-h-full">
            <MobileHeader title="Party Details" showBack />

            <div className="flex-1 mobile-scroll-area pb-tab-bar">
                <div className="p-4 space-y-6">
                    {/* Header Card */}
                    <div className="mobile-card flex flex-col items-center py-6">
                        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                            <span className="text-2xl font-bold text-primary">
                                {party.name.charAt(0).toUpperCase()}
                            </span>
                        </div>
                        <h2 className="text-xl font-bold text-foreground mb-1 text-center">
                            {party.name}
                        </h2>
                        <MobileBadge variant="secondary">
                            {party.primary_party_type}
                        </MobileBadge>
                    </div>

                    {/* Contact Info */}
                    {(party.phone || party.email) && (
                        <div>
                            <h2 className="mobile-section-header">Contact</h2>
                            <MobileCardGroup>
                                {party.phone && (
                                    <MobileCardRow onClick={handleCall} showArrow>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                                                <Phone className="h-5 w-5 text-green-500" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Phone</p>
                                                <p className="font-medium">{party.phone}</p>
                                            </div>
                                        </div>
                                    </MobileCardRow>
                                )}

                                {party.email && (
                                    <MobileCardRow onClick={handleEmail} showArrow>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                                                <Mail className="h-5 w-5 text-blue-500" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Email</p>
                                                <p className="font-medium">{party.email}</p>
                                            </div>
                                        </div>
                                    </MobileCardRow>
                                )}
                            </MobileCardGroup>
                        </div>
                    )}

                    {/* Addresses */}
                    {(party.billing_address || party.shipping_address || party.geo_location) && (
                        <div>
                            <h2 className="mobile-section-header">Addresses</h2>
                            <MobileCardGroup>
                                {party.billing_address && (
                                    <MobileCardRow>
                                        <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                                                <MapPin className="h-5 w-5 text-muted-foreground" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Billing Address</p>
                                                <p className="font-medium whitespace-pre-line">{party.billing_address}</p>
                                            </div>
                                        </div>
                                    </MobileCardRow>
                                )}

                                {party.shipping_address && party.has_separate_shipping && (
                                    <MobileCardRow>
                                        <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                                                <MapPin className="h-5 w-5 text-muted-foreground" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Shipping Address</p>
                                                <p className="font-medium whitespace-pre-line">{party.shipping_address}</p>
                                            </div>
                                        </div>
                                    </MobileCardRow>
                                )}

                                {party.geo_location && (
                                    <MobileCardRow onClick={handleOpenMaps} showArrow>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                                                <Navigation className="h-5 w-5 text-red-500" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Location</p>
                                                <p className="font-medium text-primary">Open in Maps</p>
                                            </div>
                                        </div>
                                    </MobileCardRow>
                                )}
                            </MobileCardGroup>
                        </div>
                    )}

                    {/* Recent Orders */}
                    <div>
                        <h2 className="mobile-section-header">Recent Orders</h2>
                        <MobileCardGroup>
                            {orderLoading ? (
                                <>
                                    {[1, 2, 3].map((i) => (
                                        <MobileCardRow key={i}>
                                            <div className="flex items-center gap-3">
                                                <MobileSkeleton className="w-10 h-10 rounded-full" />
                                                <div className="space-y-2 flex-1">
                                                    <MobileSkeleton className="h-4 w-24" />
                                                    <MobileSkeleton className="h-4 w-32" />
                                                </div>
                                            </div>
                                        </MobileCardRow>
                                    ))}
                                </>
                            ) : recentOrders.length > 0 ? (
                                <>
                                    {recentOrders.map((order) => {
                                        const isPaid = order.payment_status === 'Paid';
                                        const isPartial = order.payment_status === 'Partially Paid';
                                        return (
                                        <MobileCardRow key={order.id}>
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                                    <ShoppingBag className="h-5 w-5 text-primary" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <p className="text-sm font-medium truncate">
                                                            #{order.voucher_number}
                                                        </p>
                                                        <p className="font-semibold text-primary whitespace-nowrap">
                                                            ₹{parseFloat(order.total_amount).toLocaleString()}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center justify-between mt-0.5">
                                                        <p className="text-xs text-muted-foreground">
                                                            {format(new Date(order.transaction_date), 'PPP')}
                                                        </p>
                                                        {order.payment_status && (
                                                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                                                                isPaid
                                                                    ? 'bg-green-500/10 text-green-500'
                                                                    : isPartial
                                                                        ? 'bg-yellow-500/10 text-yellow-500'
                                                                        : 'bg-red-500/10 text-red-500'
                                                            }`}>
                                                                {isPartial && order.amount_due
                                                                    ? `Due ₹${parseFloat(order.amount_due).toLocaleString()}`
                                                                    : order.payment_status}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </MobileCardRow>
                                        );
                                    })}
                                </>
                            ) : (
                                <MobileCardRow>
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                                            <ShoppingBag className="h-5 w-5 text-muted-foreground" />
                                        </div>
                                        <p className="text-muted-foreground">No orders yet</p>
                                    </div>
                                </MobileCardRow>
                            )}
                        </MobileCardGroup>
                    </div>

                    {/* Credit Info */}
                    <div>
                        <h2 className="mobile-section-header">Credit Information</h2>
                        <MobileCardGroup>
                            <MobileCardRow>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
                                        <CreditCard className="h-5 w-5 text-yellow-500" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground">Credit Limit</p>
                                        <p className="font-medium">{creditLimitDisplay}</p>
                                    </div>
                                </div>
                            </MobileCardRow>
                        </MobileCardGroup>
                    </div>

                    {/* Additional Info */}
                    {(party.party_group || party.site) && (
                        <div>
                            <h2 className="mobile-section-header">Additional Info</h2>
                            <MobileCardGroup>
                                {party.party_group && (
                                    <MobileCardRow>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                                                <Users className="h-5 w-5 text-muted-foreground" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Party Group</p>
                                                <p className="font-medium">{party.party_group.name}</p>
                                            </div>
                                        </div>
                                    </MobileCardRow>
                                )}

                                {party.site && (
                                    <MobileCardRow>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                                                <Building className="h-5 w-5 text-muted-foreground" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Site</p>
                                                <p className="font-medium">{party.site.name}</p>
                                            </div>
                                        </div>
                                    </MobileCardRow>
                                )}
                            </MobileCardGroup>
                        </div>
                    )}

                    {/* Record */}
                    <div>
                        <h2 className="mobile-section-header">Record</h2>
                        <MobileCardGroup>
                            <MobileCardRow>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                                        <Calendar className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground">Created</p>
                                        <p className="font-medium">
                                            {format(new Date(party.created_at), 'MMMM d, yyyy')}
                                        </p>
                                    </div>
                                </div>
                            </MobileCardRow>
                        </MobileCardGroup>
                    </div>
                </div>
            </div>
        </div>
    );
}
